import { IconButton, type SelectItem } from '@vrplatform/components';
import {
  isTransactionLinePropertyOptionalForAccount,
  useAccountsQuery,
  useQuery,
} from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import { LoadingIndicator } from '@vrplatform/components-legacy';
import { toTitleCase } from '@vrplatform/utils';
import { Input, InputBase } from '@mantine/core';
import { Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Controller, useWatch } from 'react-hook-form';
import { useExpenseForm } from '../../useExpenseForm';
import { ExpenseListingReservationModal } from './ExpenseListingReservationModal';

type SelectType = 'listing' | 'reservation';

export const ExpenseListingReservationSelect = ({
  index,
  disabled,
}: {
  index: number;
  disabled: boolean;
}) => {
  const methods = useExpenseForm();
  const { data: accounts = [] } = useAccountsQuery();

  const pattern = useWatch({
    control: methods.control,
    name: 'pattern',
  });

  const reservationId = methods.watch(`lines.${index}.reservationId`);
  const listingId = methods.watch(`lines.${index}.listingId`);

  const type: SelectType =
    pattern || !reservationId ? 'listing' : 'reservation';

  return (
    <Controller
      control={methods.control}
      name={`lines.${index}.reservationId`}
      rules={{
        // required: isBillToManager
        //   ? false
        //   : `${type === 'reservation' ? 'Reservation' : 'Listing'} is required`,
        validate: (reservationId, formValues) => {
          const isBillToManager = formValues.lines[index].party === 'manager';

          if (isBillToManager) return true;

          const value = reservationId || formValues.lines[index].listingId;

          const currentAccountId = formValues.lines[index].accountId;
          const currentAccount = accounts.find(
            (account) => account.id === currentAccountId
          );

          if (isTransactionLinePropertyOptionalForAccount(currentAccount)) {
            return true;
          }

          if (!value) return `${toTitleCase(type)} is required`;

          return true;
        },
      }}
      render={({ fieldState, field, formState }) => {
        const [opened, handlers] = useDisclosure(false);

        const id = reservationId || listingId;

        const { data, isLoading: loading } = useQuery(
          (q, args) => {
            if (!args.value)
              return {
                item: null,
              };

            const reservation = q
              .reservations({
                where: { id: { _eq: args.value } },
                limit: 1,
              })
              .map<SelectItem>((item) => ({
                label: item.guestName || 'No name',
                value: item.id,
              }))[0];

            const listing = q
              .listings({
                where: {
                  id: { _eq: args.value },
                },
                limit: 1,
              })
              .map<SelectItem>((item) => ({
                label: item.calculatedTitle || 'No name',
                value: item.id,
              }))[0];

            const item = listing || reservation || null;

            return {
              item,
            };
          },
          {
            keepPreviousData: true,
            variables: {
              value: id,
            },
          }
        );

        const currentValue = id ? data?.item || null : null;

        const activeEntity = id
          ? {
              id,
              type,
            }
          : null;

        return (
          <>
            <ExpenseListingReservationModal
              opened={opened}
              closeModal={handlers.close}
              activeEntity={activeEntity}
              type={pattern ? 'listing' : undefined}
              handleSubmit={(entity) => {
                const isError = !!(
                  formState.errors.lines?.[index]?.reservationId ||
                  formState.errors.lines?.[index]?.listingId
                );

                methods.setValue(
                  `lines.${index}.reservationId`,
                  entity.type === 'reservation' ? entity.id : null,
                  {
                    shouldDirty: true,
                    shouldTouch: true,
                    shouldValidate: isError,
                  }
                );
                methods.setValue(
                  `lines.${index}.listingId`,
                  entity.type === 'listing' ? entity.id : null,
                  {
                    shouldDirty: true,
                    shouldTouch: true,
                    shouldValidate: isError,
                  }
                );
              }}
            />

            <Tooltip
              label={currentValue?.description}
              disabled={!currentValue?.description || opened}
              withinPortal
              withArrow
            >
              <InputBase
                component="button"
                error={fieldState.error?.message}
                type="button"
                pointer
                data-testid="listing-reservation-select"
                disabled={
                  field.disabled ||
                  methods.context.lock?.expense === 'locked' ||
                  disabled
                }
                rightSection={
                  currentValue?.label &&
                  methods.context.lock?.expense !== 'locked' ? (
                    <IconButton
                      icon="CrossIcon"
                      onClick={() => {
                        field.onChange(null);
                        methods.setValue(`lines.${index}.listingId`, null, {
                          shouldDirty: true,
                          shouldTouch: true,
                        });
                        methods.setValue(`lines.${index}.reservationId`, null, {
                          shouldDirty: true,
                          shouldTouch: true,
                        });
                      }}
                      color={(theme) => theme.colors.neutral[4]}
                      mt={2}
                      iconSize={16}
                      ml={2}
                    />
                  ) : (
                    <Icon
                      icon="ChevronIcon"
                      size={18}
                      color={(theme) => theme.colors.gray[4]}
                    />
                  )
                }
                radius="md"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  handlers.toggle();
                }}
                rightSectionPointerEvents={
                  currentValue?.label ? undefined : 'none'
                }
                sx={(theme) => {
                  const activeStyles = {
                    boxShadow: `0px 0px 0px 2px ${
                      theme.colors[
                        fieldState.error ? 'red' : theme.primaryColor
                      ][4]
                    }40`,
                    borderColor: fieldState.error
                      ? theme.colors.red[6]
                      : theme.colors[theme.primaryColor][6],
                  };

                  return {
                    width: '100%',
                    button: {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      '&:focus': activeStyles,
                    },
                    '.mantine-Input-input': {
                      ...(opened ? activeStyles : {}),
                      '&:disabled': { color: 'unset' },
                    },
                  };
                }}
                leftSection={
                  loading ? (
                    <LoadingIndicator size="xs" />
                  ) : (
                    <Icon
                      icon={
                        type === 'reservation'
                          ? 'CalendarEventIcon'
                          : 'HomeIcon'
                      }
                      mt={1}
                      size={16}
                    />
                  )
                }
              >
                {currentValue ? (
                  <Input.Label fw={400}>{currentValue.label}</Input.Label>
                ) : (
                  <Input.Placeholder>
                    Select {pattern ? 'listing' : 'listing/reservation'}
                  </Input.Placeholder>
                )}
              </InputBase>
            </Tooltip>
          </>
        );
      }}
    />
  );
};
